/**
 * One central realtime subscription — mounted ONCE, in `Layout.tsx` (the app shell; it renders
 * once for the whole session, not per screen, since child routes swap inside its `<Outlet/>`).
 * Nothing else calls `useFrappeEventListener` — a second call site would double-subscribe.
 *
 * ---- Why invalidation is by DOMAIN ROOT, not by the id the payload names ----
 *
 * The event NAMES are the one thing live-verified: `mail.get_mailbox_info`'s
 * `frappe_realtime_events` (`useMail.ts`'s `WireMailboxInfo`, checked 2026-08-04) lists exactly
 * these four on the real server. The PAYLOAD each event carries is not verified, and two docs
 * disagree on its shape:
 *
 *   - `src/lib/types.ts`'s `RealtimeEvent` union:
 *       mail.new       { account, mail_id }
 *       sq.received    { enquiry_id, sq_id, supplier }
 *       extract.done   { enquiry_id, item_count }
 *       enquiry.status { enquiry_id, status }
 *   - `docs/bosun-email-api-infra.md` (design doc; CLAUDE.md marks it "context only, don't
 *     implement"):
 *       mail.new       { account, id, from_name, subject, req_tag }
 *       sq.received    { enquiry_id, rfq_id, supplier }
 *       enquiry.status { id, status }
 *
 * `docs/bosun-frappe-contract.md` §11 marks the whole feature `PROPOSED` — "No event names are
 * documented in the collections" — and Q13 (`docs/bosun-frappe-open-questions.md`) is the open
 * ask to the backend team. Coding a SCOPED invalidation ("only refetch comparison for THIS
 * enquiry_id") means picking one of the two field names above and pinning it with a unit test —
 * which would tell the next reader this is settled when it is a guess in either direction. So
 * this handler reads NO field off any payload. It maps event NAME to a set of ROOT query keys
 * and invalidates broadly:
 *
 *   mail.new       → qk.mailRoot()        (every mail query refetches)
 *   sq.received    → qk.enquiriesRoot()   (supplier quotations AND comparison hang off it)
 *   extract.done   → qk.enquiriesRoot()   (the enquiry the extraction landed on)
 *   enquiry.status → qk.enquiriesRoot()   (the board list AND the one enquiry)
 *
 * `realtimeInvalidationKeys` below is the pure, pinned part — `useRealtime.test.ts`, the same
 * `folderBadge`/`buildQuotationOutcome` pattern this repo uses everywhere else, since there is no
 * component-test infrastructure here to mount `Layout` and fire a socket event at it. Once a
 * payload shape is confirmed, narrowing this to a scoped key is a SAFE follow-up change; guessing
 * wrong here up front would not have been — a passing test for a guessed shape reads as settled
 * to the next person, and it is not.
 *
 * ---- Evidence, not another guess ----
 *
 * On the FIRST receipt of each event name (per mount), dev builds `console.debug` the raw
 * payload untouched. The point is to learn the real shape from a running system instead of
 * assuming one. Logged once per name, not once per event, so a chatty event does not flood the
 * console. See `docs/bosun-required-api-round-2.md` §D for the question this raises with the
 * backend team.
 *
 * ---- The mock emits none of this ----
 *
 * `docs/bosun-frappe-contract.md` §11 used to claim "the mock serves a plain WebSocket on
 * `/socket.io` emitting those four event names" — checked while building this file, and false:
 * `mock-server/` has no `socket.io` dependency, no socket server, and publishes nothing (see
 * `mock-server/routes/enquiry-writes.ts`'s own comment: "the mock has no queue and no socket").
 * That doc line is corrected alongside this file. `main.tsx` reflects the true state — socket
 * enabled by default only when `BOSUN_LIVE_URL` is configured, off against the mock.
 *
 * Building a mock socket server is a real feature (a long-lived process, an emit call threaded
 * through every write route that should fire one, reconnect/backoff semantics) — more than the
 * "small change" the dispatch for this file allowed, so it is NOT added here. Realtime is
 * LIVE-ONLY today: this hook subscribes unconditionally, but against the mock (`BOSUN_LIVE_URL`
 * unset) it will simply never fire — `main.tsx` now defaults `enableSocket` ON whenever
 * `BOSUN_LIVE_URL` IS set (see its `VITE_LIVE_BACKEND` note), so this hook is live by default in
 * that case, not opt-in. Local dev's freshness guarantee against the mock is entirely the
 * per-screen polling that already exists — `useMail.ts`'s `MAIL_POLL_MS`,
 * `useSupplierQuotations.ts`'s `SQ_POLL_MS`, `RequirementsPage.tsx`'s `BOARD_POLL_MS` — none of
 * which this file touches or gates.
 *
 * ---- `extract.done` also feeds a second, additive consumer ----
 *
 * `useMailExtraction.ts`'s "Read this mail with AI" now depends on `extract.done` to know when a
 * queued AI extraction job has finished — the collection documents that method as ASYNC
 * (`{job_id}` immediately, `extract.done` with `{item_count, items}` on completion), which this
 * repo initially built as if it were synchronous. That payload shape, unlike the other three, is
 * confirmed in the same Postman collection every other confirmed shape in this migration came
 * from — so reading it there is evidence-based, not the guess this file's invalidation
 * deliberately avoids. This handler forwards the raw `extract.done` payload to
 * `./extractDoneBus`'s `publishExtractDone` alongside its own invalidation, so
 * `useMailExtraction.ts` can react to it without a second `useFrappeEventListener` call site —
 * see `extractDoneBus.ts`'s header for why a second one would be actively dangerous. This does
 * NOT change the ruling above: `realtimeInvalidationKeys` still reads no payload field, for the
 * other three events and for this one.
 *
 * ---- Degrading silently ----
 *
 * `useFrappeEventListener` (frappe-react-sdk) already no-ops when `enableSocket` is false or the
 * socket has not connected: it reads `socket?.on(...)`, warns via `console.warn`, never throws.
 * Nothing here adds a throw path on top of that, and unmount cleanup (subscribe/unsubscribe) is
 * the SDK's own effect, not reimplemented here.
 */

import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import { useFrappeEventListener } from 'frappe-react-sdk'
import { qk } from './keys'
import { publishExtractDone } from './extractDoneBus'
import type { RealtimeEvent } from '../types'

export type RealtimeEventName = RealtimeEvent['event']

/** Every event name this hook subscribes to — used by the exhaustiveness test, not by the hook
 * itself (the four `useFrappeEventListener` calls below are written out, not looped, since hooks
 * must be called unconditionally in the same order on every render). */
export const REALTIME_EVENT_NAMES: readonly RealtimeEventName[] = [
  'mail.new',
  'sq.received',
  'extract.done',
  'enquiry.status',
]

/**
 * Pure — the pinned part. Event NAME → root query keys to invalidate. Deliberately does not
 * accept or read a payload; see the module header for why.
 */
export function realtimeInvalidationKeys(eventName: RealtimeEventName): readonly QueryKey[] {
  switch (eventName) {
    case 'mail.new':
      return [qk.mailRoot()]
    case 'sq.received':
    case 'extract.done':
    case 'enquiry.status':
      return [qk.enquiriesRoot()]
    default: {
      const unreachable: never = eventName
      return unreachable
    }
  }
}

export function useRealtime(): void {
  const queryClient = useQueryClient()
  // Which event names have already been debug-logged this mount — cleared on unmount so a later
  // remount (StrictMode's dev double-invoke, or a genuine future remount) logs fresh rather than
  // staying silent forever off a ref nothing ever reset.
  const loggedEventNames = useRef<Set<RealtimeEventName>>(new Set())
  useEffect(() => {
    const seen = loggedEventNames.current
    return () => seen.clear()
  }, [])

  // Stable across re-renders (queryClient is one instance for the app's lifetime), so the SDK's
  // `useFrappeEventListener` — whose effect deps are `[eventName, callback]` — does not tear down
  // and resubscribe on every unrelated re-render of `Layout`. That is the actual guard against
  // duplicate/flapping subscriptions; being mounted exactly once in `Layout` is the guard against
  // two independent subscriptions existing at all.
  const handle = useCallback(
    (eventName: RealtimeEventName, payload: unknown) => {
      if (import.meta.env.DEV && !loggedEventNames.current.has(eventName)) {
        loggedEventNames.current.add(eventName)
        // eslint-disable-next-line no-console -- deliberate: the one way to learn the real
        // payload shape from a running system instead of guessing (see module header).
        console.debug(`[bosun realtime] first "${eventName}" payload:`, payload)
      }
      // Additive, not a replacement for the payload-free invalidation below — see the module
      // header. `useMailExtraction.ts` validates this itself before trusting anything in it.
      if (eventName === 'extract.done') publishExtractDone(payload)
      for (const key of realtimeInvalidationKeys(eventName)) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
    [queryClient],
  )

  const onMailNew = useCallback((payload: unknown) => handle('mail.new', payload), [handle])
  const onSqReceived = useCallback((payload: unknown) => handle('sq.received', payload), [handle])
  const onExtractDone = useCallback((payload: unknown) => handle('extract.done', payload), [handle])
  const onEnquiryStatus = useCallback((payload: unknown) => handle('enquiry.status', payload), [handle])

  useFrappeEventListener('mail.new', onMailNew)
  useFrappeEventListener('sq.received', onSqReceived)
  useFrappeEventListener('extract.done', onExtractDone)
  useFrappeEventListener('enquiry.status', onEnquiryStatus)
}
